import React, { useState } from 'react'
import { UserService } from '@duck/api-definitions'
import {
  Badge,
  MultiPick,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@duck/ui'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { TranslationKey } from '@/modules/i18n/types'
import { useTar } from '@/modules'
import { useOrganizations } from '@/storeHooks/useOrganizations'
import { UserDataInput } from '../DataInputs/UserDataInput'

interface InvitationFormProps {
  formData: Partial<UserService.Invitation> & { sendEmails?: boolean }
  onChange: (field: keyof UserService.Invitation | 'sendEmails', value: string | number | string[] | boolean | undefined) => void
  onSave: () => void
  onCancel: () => void
  isSaving?: boolean
  saveError?: string | null
  isEditing?: boolean
  isReadOnly?: boolean
  allAccessGroups?: string[]
}

export const InvitationForm: React.FC<InvitationFormProps> = ({
  formData,
  onChange,
  onSave,
  onCancel,
  isSaving = false,
  saveError,
  isEditing = false,
  isReadOnly = false,
  allAccessGroups = [],
}) => {
  const { t } = useTar()
  const [isPartnerInvitation, setIsPartnerInvitation] = useState(!!formData.organizationId)

  const { organizations: allOrganizations = [] } = useOrganizations()
  // Handle inviterId change - auto-fill inviterName when user is selected
  const handleInviterChange = (selectedIds: string[]) => {
    onChange('inviterId', selectedIds[0] || '')
  }
  const renderFormField = (
    label: TranslationKey,
    field: keyof UserService.Invitation,
    type: 'text' | 'email' | 'number' | 'select' | 'checkbox-group' = 'text',
    required = false,
    options?: readonly { value: string; label: TranslationKey }[],
    disabled = false
  ) => {
    const value = formData[field] || (type === 'checkbox-group' ? [] : '')
    const baseInputClasses = `
      w-full px-3 py-2 text-sm border border-border rounded-md 
      bg-card text-foreground placeholder-secondary/60
      focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
      transition-colors
      ${disabled ? 'bg-secondary/5 text-muted-foreground cursor-not-allowed' : 'hover:border-secondary'}
    `

    // Render read-only text when in read-only mode
    if (isReadOnly) {
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-muted-foreground">
            {t(label)}
          </label>
          {field === 'accessGroups' ? (
            <div>
              {Array.isArray(value) && value.length > 0 ? (
                <div className="flex flex-wrap gap-1 border-none">
                  {value.map((group: string, index: number) => (
                    <Badge key={index} variant="outline" className="bg-accent/10 text-accent border-accent/20">
                      {group}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground italic">{t('common.noGroups')}</span>
              )}
            </div>
          ) : type === 'checkbox-group' && options ? (
            <div className="text-foreground">
              {Array.isArray(value) && value.length > 0
                ? options.filter(opt => value.includes(opt.value as string))
                  .map(opt => t(opt.label)).join(', ')
                : t('common.none')
              }
            </div>
          ) : type === 'select' && options ? (
            <div className="text-foreground">
              {t(options.find(opt => opt.value === value)?.label ?? 'common.na')}
            </div>
          ) : type === 'number' ? (
            <div className="text-foreground">
              {value ?? t('common.na')}
            </div>
          ) : (
            <div className="text-foreground">
              {value ?? t('common.na')}
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">
          {t(label)}
          {required && <span className="text-error ms-1">*</span>}
        </label>
        {type === 'checkbox-group' && options ? (
          <div className="space-y-2">
            {options.map((option) => {
              const isChecked = Array.isArray(value) && value.includes(option.value as string)
              return (
                <label key={option.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const currentArray = Array.isArray(value) ? value : []
                      if (e.target.checked) {
                        onChange(field, [...currentArray, option.value])
                      } else {
                        onChange(field, currentArray.filter(v => v !== option.value))
                      }
                    }}
                    disabled={disabled}
                    className="h-4 w-4 text-accent focus:ring-accent border-border rounded"
                  />
                  <span className="text-sm text-foreground">{t(option.label)}</span>
                </label>
              )
            })}
          </div>
        ) : type === 'select' && options ? (
          <select
            value={value as string}
            onChange={(e) => onChange(field, e.target.value)}
            disabled={disabled}
            className={baseInputClasses}
          >
            <option value="">{t('common.select')} {t(label).toLowerCase()}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === 'number' ? (
          <input
            type="number"
            value={value as number}
            onChange={(e) => onChange(field, parseFloat(e.target.value) || 0)}
            disabled={disabled}
            className={baseInputClasses}
            placeholder={t('common.enterValue', { value: t(label).toLowerCase() })}
            min="1"
          />
        ) : (
          <input
            type={type === 'email' ? 'email' : 'text'}
            value={value as string}
            onChange={(e) => onChange(field, e.target.value)}
            disabled={disabled || (isEditing && field === 'email')}
            className={baseInputClasses}
            placeholder={t('common.enterValue', { value: t(label).toLowerCase() })}
          />
        )}
      </div>
    )
  }

  // Update isPartnerInvitation when formData.organizationId changes
  React.useEffect(() => {
    setIsPartnerInvitation(!!formData.organizationId)
  }, [formData.organizationId])

  const handlePartnerInvitationChange = (checked: boolean) => {
    setIsPartnerInvitation(checked)
    if (checked) {
      // Set role to partner when checking partner invitation
      onChange('role', UserService.UserRole.Partner)
      onChange('accessGroups', [])
    } else {
      // Clear role when unchecking
      onChange('role', undefined)
    }
  }

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.basicInformation')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="lg:col-span-2">
            {renderFormField('common.emailAddress', 'email', 'email', true, undefined, isEditing)}
          </div>
          <div className="lg:col-span-1">
            {renderFormField('common.firstName', 'firstName', 'text', false)}
          </div>
          <div className="lg:col-span-1">
            {renderFormField('common.lastName', 'lastName', 'text', false)}
          </div>

          {/* Partner Invitation Checkbox */}
          {!isReadOnly && (
            <div className="lg:col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isPartnerInvitation}
                  onChange={(e) => handlePartnerInvitationChange(e.target.checked)}
                  disabled={isSaving}
                  className="h-4 w-4 text-accent focus:ring-accent border-border rounded"
                />
                <span className="text-sm font-medium text-foreground">
                  {t('invitations.partnerInvitation')}
                </span>
              </label>
            </div>
          )}

          {/* Send Emails Switch - only show when creating new invitation */}
          {!isReadOnly && !isEditing && (
            <div className="lg:col-span-2">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('invitations.sendInvitationEmail')}
                </label>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.sendEmails !== false}
                    onCheckedChange={(checked) => onChange('sendEmails', checked)}
                    disabled={isSaving}
                  />
                  <span className="text-sm text-foreground">
                    {formData.sendEmails !== false ? t('common.yes') : t('common.no')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Organization Select - shown when Partner Invitation is checked */}
          {isPartnerInvitation && (
            <div className="lg:col-span-2">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('common.organization')}
                  <span className="text-error ms-1">*</span>
                </label>
                {isReadOnly ? (
                  <div className="text-foreground">
                    {formData.organizationId
                      ? allOrganizations.find(org => org.id === formData.organizationId)?.name || formData.organizationId
                      : t('common.na')}
                  </div>
                ) : (
                  <Select
                    value={formData.organizationId || ''}
                    onValueChange={(value) => onChange('organizationId', value)}
                    disabled={isSaving}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {allOrganizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}

          {/* Custom Body - hidden when Partner Invitation is checked */}
          {!isPartnerInvitation && (
            <div className="lg:col-span-2">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('invitations.customBody')}
                </label>
                {isReadOnly ? (
                  <div className="text-foreground whitespace-pre-wrap">
                    {formData.customBody ?? t('common.na')}
                  </div>
                ) : (
                  <Textarea
                    value={formData.customBody || ''}
                    onChange={(e) => onChange('customBody', e.target.value)}
                    disabled={isSaving}
                    placeholder={t('common.enterValue', { value: t('invitations.customBody').toLowerCase() })}
                    className="min-h-[120px] resize-y"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Inviter Information - hidden when Partner Invitation is checked */}
      {!isPartnerInvitation && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
            {t('invitations.inviterInformation')}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="lg:col-span-1">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('invitations.inviterId')}
                </label>
                {isReadOnly ? (
                  <div className="text-foreground">
                    {formData.inviterId ?? t('common.na')}
                  </div>
                ) : (
                  <UserDataInput
                    selected={formData.inviterId ? [formData.inviterId] : []}
                    onChange={handleInviterChange}
                    onChangeFullObjects={(users) => {
                      const user = users[0]
                      onChange('inviterName', user ? `${user.firstName} ${user.lastName}`.trim() : '')
                    }}
                    singlePick
                    disabled={isSaving}
                    labels={{ placeholder: `${t('common.select')} ${t('invitations.inviterId').toLowerCase()}` }}
                  />
                )}
              </div>
            </div>
            <div className="lg:col-span-1">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('invitations.inviterName')}
                </label>
                {isReadOnly ? (
                  <div className="text-foreground">
                    {formData.inviterName ?? t('common.na')}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData.inviterName || ''}
                    onChange={(e) => onChange('inviterName', e.target.value)}
                    disabled={isSaving}
                    className={`
                      w-full px-3 py-2 text-sm border border-border rounded-md
                      bg-card text-foreground placeholder-secondary/60
                      focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
                      transition-colors hover:border-secondary
                    `}
                    placeholder={t('common.enterValue', { value: t('invitations.inviterName').toLowerCase() })}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Access Configuration - hidden when Partner Invitation is checked */}
      {!isPartnerInvitation && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
            {t('invitations.accessConfiguration')}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="lg:col-span-1">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {t('common.accessGroups')}
                  <span className="text-error ms-1">*</span>
                </label>
                {isReadOnly ? (
                  <div className="w-full min-h-[42px] px-3 py-2 border border-border rounded-md bg-card flex flex-wrap gap-1 items-center">
                    {Array.isArray(formData.accessGroups) && formData.accessGroups.length > 0 ? (
                      formData.accessGroups.map((group, index) => (
                        <Badge key={index} variant="outline" className="bg-accent/10 text-accent border-accent/20">
                          {group}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-xs text-muted-foreground italic">{t('common.noGroups')}</span>
                    )}
                  </div>
                ) : (
                  <MultiPick
                    options={allAccessGroups.map((g) => ({ label: g, value: g }))}
                    selected={Array.isArray(formData.accessGroups) ? (formData.accessGroups as string[]) : []}
                    onChange={(vals) => onChange('accessGroups', vals)}
                    labels={{ placeholder: t('common.pickAccessGroups') }}
                    allowNewItem
                  />
                )}
              </div>
            </div>
            <div className="lg:col-span-1">
              <div className="space-y-4">
                {!isEditing && renderFormField('invitations.validFor', 'validFor', 'number', true)}
                {isEditing && (
                  <>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-muted-foreground">{t('invitations.validFor')}</label>
                      <p className="text-foreground">{formData.validFor ?? t('common.na')} {t('common.days')}</p>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-foreground">
                        {t('invitations.revokeInvitation')}
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.status === 'revoked'}
                          onChange={(e) => {
                            if (e.target.checked) {
                              onChange('status', 'revoked')
                            } else {
                              // Reset to active if unchecked (you may want to track previous status)
                              onChange('status', 'active')
                            }
                          }}
                          className="h-4 w-4 text-accent focus:ring-accent border-border rounded"
                        />
                        <span className="text-sm text-foreground">{t('invitations.revokeInvitation')}</span>
                      </label>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invitation Details (Read-only for editing) */}
      {isEditing && formData.id && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
            {t('invitations.invitationDetails')}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <label className="block font-medium text-muted-foreground">{t('invitations.invitationId')}</label>
              <p className="text-foreground font-mono">{formData.id ?? t('common.na')}</p>
            </div>
            <div className="space-y-2">
              <label className="block font-medium text-muted-foreground">{t('common.createdAt')}</label>
              <p className="text-foreground">
                {formData.createdAt ? (
                  <RelativeDateTime isoDateString={formData.createdAt} />
                ) : t('common.na')}
              </p>
            </div>
            <div className="space-y-2">
              <label className="block font-medium text-muted-foreground">{t('common.expiresAt')}</label>
              <p className="text-foreground">
                {formData.expiresAt ? (
                  <RelativeDateTime isoDateString={formData.expiresAt} />
                ) : t('common.na')}
              </p>
            </div>
            {formData.registeredUserId && (
              <div className="space-y-2">
                <label className="block font-medium text-muted-foreground">{t('common.registeredUserId')}</label>
                <p className="text-foreground">{formData.registeredUserId ?? t('common.na')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {saveError && (
        <div className="p-3 bg-error/10 border border-error/20 rounded-md">
          <p className="text-sm text-error">{saveError}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border justify-end">
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-md hover:bg-secondary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isReadOnly ? t('common.close') : t('common.cancel')}
        </button>
        {!isReadOnly && (
          <button
            onClick={onSave}
            disabled={isSaving}
            className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {isSaving ? t('common.saving') : isEditing ? t('invitations.updateInvitation') : t('invitations.sendInvitation')}
          </button>
        )}
      </div>
    </div>
  )
}
